import { useMemo } from "react"
import { useTheme } from "@mui/material"

import type { SxProps } from "@mui/material"

import type { TowerViewStyle } from "types/devicePosition.types"

import { AngleViewLayout } from "./AngleViewLayout"

interface TowerAngleViewProps {
  src: string
  value: number
  viewKey: "first" | "second"
  displayName: string
  viewStyle: TowerViewStyle
  rowHeight: number
  alt?: string
}

export const TowerAngleView = ({
  src,
  viewStyle,
  viewKey,
  displayName,
  value,
  rowHeight,
  alt,
}: TowerAngleViewProps) => {
  const theme = useTheme()
  const isFirstView = viewKey === "first"

  const imgBoxStyles = useMemo<SxProps>(() => {
    switch (viewStyle) {
      case "tower-relative":
        return {
          height: `${rowHeight * 3.5}px`,
          [theme.breakpoints.up("sm")]: {
            height: `${rowHeight * 3.7}px`,
          },
        }
      case "v4":
      case "v4-legacy":
      case "v4-pole-legacy":
      case "v4-hva":
      case "v4-mast":
        return {
          height: `${rowHeight * 3.5}px`,
          [theme.breakpoints.up("sm")]: {
            height: `${rowHeight * 3.7}px`,
          },
        }
      case "v2v3":
      default:
        return {
          height: `${rowHeight * 4}px`,
          [theme.breakpoints.up("sm")]: {
            height: `${rowHeight * 5}px`,
          },
        }
    }
  }, [viewStyle, rowHeight, theme.breakpoints])

  const imgStyles = useMemo<SxProps>(() => {
    const rotate = `rotate(${-value}deg)`
    const absValue = Math.abs(value)
    switch (viewStyle) {
      case "tower-relative":
        return isFirstView
          ? {
              transform: rotate,
              transformOrigin: "50% 50%",
              translate: "0 2rem",
              height: "90%",
              width: "fit-content",
              objectFit: "contain" as const,
            }
          : {
              transform: rotate,
              transformOrigin: "50% 50%",
              translate: "0 2rem",
              height: "90%",
              width: "fit-content",
              objectFit: "contain" as const,
            }
      case "v4-pole-legacy":
        return isFirstView
          ? {
              transform: rotate,
              transformOrigin: "50% 50%",
              translate: `${absValue > 90 ? "0 2rem" : "0 3rem"}`,
              height: "85%",
              width: "fit-content",
              objectFit: "contain" as const,
            }
          : {
              transform: rotate,
              transformOrigin: "50% 50%",
              translate: `${absValue >= 70 ? "1rem 2rem" : "-3rem 2rem"}`,
              height: "85%",
              width: "fit-content",
              objectFit: "contain" as const,
            }
      case "v4":
      case "v4-legacy":
      case "v4-hva":
      case "v4-mast":
        return isFirstView
          ? {
              transform: rotate,
              transformOrigin: "50% 50%",
              translate: `${absValue > 90 ? "0 2rem" : "0 3rem"}`,
              height: "90%",
              width: "fit-content",
              objectFit: "contain" as const,
            }
          : {
              transform: rotate,
              transformOrigin: "50% 20%",
              translate: `${absValue > 90 ? "0 10rem" : "0 2rem"}`,
              height: "90%",
              width: "fit-content",
              objectFit: "contain" as const,
            }
      case "v2v3":
      default:
        return isFirstView
          ? {
              transform: rotate,
              transformOrigin: "50% 70%",
              translate: `${absValue > 90 ? "0 12rem" : "0 3rem"}`,
              height: "90%",
              width: "fit-content",
              objectFit: "contain" as const,
            }
          : {
              transform: rotate,
              transformOrigin: "50% 70%",
              translate: `${absValue > 90 ? "0 10rem" : "0 2rem"}`,
              margin: "3rem 0 0 0",
              height: "90%",
              width: "fit-content",
              objectFit: "contain" as const,
            }
    }
  }, [viewStyle, value, isFirstView])

  const valueBoxStyles = useMemo<SxProps>(
    () => ({
      overflow: "hidden",
      position: "absolute",
      top: "1rem",
      width: "100%",
    }),
    [],
  )

  return (
    <AngleViewLayout
      src={src}
      viewKey={viewKey}
      displayName={displayName}
      alt={alt}
      imgBoxStyles={imgBoxStyles}
      imgStyles={imgStyles}
      valueBoxStyles={valueBoxStyles}
    />
  )
}
